import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import { CalendarIcon, Clock, Anchor, Ship } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WaveDivider } from "@/components/WaveDivider";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM",
];

const bookingSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  date: z.date({ required_error: "Please select a date" }),
  time: z.string().min(1, "Please select a time"),
  food_request: z.string().min(10, "Please describe your food request (at least 10 characters)"),
});

const submitBooking = async (data) => {
  const payload = { ...data, date: format(data.date, "yyyy-MM-dd") };
  return axios.post(`${API}/booking`, payload);
};

const HeroSection = () => (
  <section
    className="relative pt-32 pb-20 md:pt-40 md:pb-28"
    style={{
      background: "linear-gradient(135deg, #00B4D8 0%, #0096C7 50%, #E91E8C 100%)",
    }}
  >
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-24 right-10 opacity-10">
        <Ship className="w-32 h-32 text-white" />
      </div>
      <div className="absolute bottom-20 left-10 opacity-10">
        <Anchor className="w-24 h-24 text-white" />
      </div>
    </div>

    <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <span className="font-accent text-3xl md:text-4xl text-white/90">
        Plan Your Event
      </span>
      <h1 className="font-heading font-bold text-5xl md:text-7xl text-white mt-2 mb-6 text-shadow">
        Book Seaside Bites
      </h1>
      <p className="font-body text-xl text-white/90 max-w-2xl mx-auto">
        Fill in the details below and someone will reach out with pricing and
        details for your water adventure!
      </p>
    </div>

    <div className="absolute bottom-0 left-0 right-0">
      <WaveDivider color="#E0FBFC" />
    </div>
  </section>
);

const SuccessMessage = ({ onReset }) => (
  <div
    data-testid="booking-success"
    className="bg-white rounded-3xl p-8 md:p-12 text-center shadow-xl"
  >
    <div className="w-20 h-20 bg-seaside-teal/20 rounded-full flex items-center justify-center mx-auto mb-6">
      <Ship className="w-10 h-10 text-seaside-teal" />
    </div>
    <h2 className="font-heading font-bold text-3xl text-seaside-navy mb-4">
      Booking Request Received!
    </h2>
    <p className="font-body text-lg text-gray-600 mb-8">
      Thank you for your interest in Seaside Bites! We'll review your
      request and reach out soon with pricing and details.
    </p>
    <Button
      data-testid="submit-another-btn"
      onClick={onReset}
      className="btn-primary px-8 py-4 font-heading font-bold rounded-full"
    >
      Submit Another Request
    </Button>
  </div>
);

const FormField = ({ label, error, children }) => (
  <div className="space-y-2">
    <Label className="font-body font-semibold text-seaside-navy">{label}</Label>
    {children}
    {error && <p className="text-red-500 text-sm">{error}</p>}
  </div>
);

const DatePickerField = ({ value, onChange, error }) => (
  <FormField label="Preferred Date *" error={error}>
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid="booking-date-picker"
          className={cn(
            "w-full justify-start text-left font-normal input-seaside py-3 h-auto",
            !value && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "PPP") : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          disabled={(date) => date < new Date()}
          initialFocus
          data-testid="booking-calendar"
        />
      </PopoverContent>
    </Popover>
  </FormField>
);

const TimePickerField = ({ value, onChange, error }) => (
  <FormField label="Preferred Time *" error={error}>
    <Select onValueChange={onChange}>
      <SelectTrigger data-testid="booking-time-select" className="input-seaside py-3 h-auto">
        <SelectValue placeholder="Select a time">
          <div className="flex items-center">
            <Clock className="mr-2 h-4 w-4" />
            {value || "Select a time"}
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent data-testid="booking-time-options">
        {TIME_SLOTS.map((time) => (
          <SelectItem key={time} value={time}>{time}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </FormField>
);

const BookingForm = ({ form, onSubmit, isSubmitting }) => {
  const { register, handleSubmit, watch, setValue, formState: { errors } } = form;

  return (
    <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
      <h2 className="font-heading font-bold text-3xl text-seaside-navy mb-2 text-center">
        Request a Booking
      </h2>
      <p className="font-body text-gray-600 text-center mb-8">
        Tell us about your event and we'll get back to you!
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <FormField label="Your Name *" error={errors.name?.message}>
          <Input
            data-testid="booking-name-input"
            placeholder="Enter your full name"
            className="input-seaside py-3"
            {...register("name")}
          />
        </FormField>

        <FormField label="Email Address *" error={errors.email?.message}>
          <Input
            type="email"
            data-testid="booking-email-input"
            placeholder="your@email.com"
            className="input-seaside py-3"
            {...register("email")}
          />
        </FormField>

        <FormField label="Phone Number *" error={errors.phone?.message}>
          <Input
            type="tel"
            data-testid="booking-phone-input"
            placeholder="(555) 123-4567"
            className="input-seaside py-3"
            {...register("phone")}
          />
        </FormField>

        <DatePickerField
          value={watch("date")}
          onChange={(date) => setValue("date", date)}
          error={errors.date?.message}
        />

        <TimePickerField
          value={watch("time")}
          onChange={(value) => setValue("time", value)}
          error={errors.time?.message}
        />

        <FormField label="Food Request & Event Details *" error={errors.food_request?.message}>
          <Textarea
            data-testid="booking-food-request"
            placeholder="Tell us about your event: number of guests, types of food you'd like, any special requests, location details, etc."
            className="input-seaside min-h-32"
            {...register("food_request")}
          />
        </FormField>

        <Button
          type="submit"
          data-testid="booking-submit-btn"
          disabled={isSubmitting}
          className="w-full btn-primary py-4 text-lg font-heading font-bold rounded-full"
        >
          {isSubmitting ? "Submitting..." : "Submit Booking Request"}
        </Button>

        <p className="text-center text-sm text-gray-500 font-body">
          We'll reach out with pricing and details. No commitment until you confirm!
        </p>
      </form>
    </div>
  );
};

const InfoCards = () => {
  const cards = [
    { icon: Ship, color: "magenta", title: "We Come To You", text: "Find us on the water around Pasco County islands and sandbars" },
    { icon: CalendarIcon, color: "teal", title: "Flexible Scheduling", text: "Let us know your preferred date and time, we'll work with you" },
    { icon: Anchor, color: "gold", title: "Custom Menus", text: "Tell us what you need, we'll create a menu that fits your event" },
  ];

  const colorClasses = {
    magenta: "bg-seaside-magenta/10 text-seaside-magenta",
    teal: "bg-seaside-teal/10 text-seaside-teal",
    gold: "bg-seaside-gold/20 text-seaside-gold",
  };

  return (
    <section className="bg-white py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          {cards.map(({ icon: Icon, color, title, text }) => (
            <div key={title} className="space-y-3">
              <div className={`w-16 h-16 ${colorClasses[color]} rounded-full flex items-center justify-center mx-auto`}>
                <Icon className="w-8 h-8" />
              </div>
              <h3 className="font-heading font-bold text-xl text-seaside-navy">{title}</h3>
              <p className="font-body text-gray-600">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function BookUsPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(bookingSchema),
    defaultValues: { name: "", email: "", phone: "", food_request: "" },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await submitBooking(data);
      setSubmitted(true);
      toast.success("Booking request submitted! We'll reach out soon with pricing and details.");
      form.reset();
    } catch {
      toast.error("Failed to submit booking. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="book-us-page" className="overflow-hidden">
      <HeroSection />
      <section className="bg-seaside-water py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <SuccessMessage onReset={() => setSubmitted(false)} />
          ) : (
            <BookingForm form={form} onSubmit={onSubmit} isSubmitting={isSubmitting} />
          )}
        </div>
      </section>
      <InfoCards />
    </div>
  );
}
